import React from 'react';
import {
  SUPPORTED_LANGUAGES,
  BUNDLED_LANGUAGES,
  LANGUAGE_LABELS,
  type SupportedLanguage,
} from './supportedLanguages';

interface LanguageSelectProps {
  value: SupportedLanguage | string;
  onChange: (lang: SupportedLanguage) => void;
  // Restrict to a caller-supplied list (e.g. `availableLanguages` from AuthContext).
  // If omitted, falls back to BUNDLED_LANGUAGES so we never offer a half-translated locale.
  available?: readonly string[];
  disabled?: boolean;
  // Include a leading "Use company default" option (maps to ''). Used by the per-user picker.
  includeUseCompanyDefault?: boolean;
  useCompanyDefaultLabel?: string;
  className?: string;
  ariaLabel?: string;
}

export const LanguageSelect: React.FC<LanguageSelectProps> = ({
  value,
  onChange,
  available,
  disabled,
  includeUseCompanyDefault,
  useCompanyDefaultLabel = 'Use company default',
  className,
  ariaLabel,
}) => {
  const offered = (available ?? BUNDLED_LANGUAGES)
    .filter((lng): lng is SupportedLanguage =>
      (SUPPORTED_LANGUAGES as readonly string[]).includes(lng)
    );

  return (
    <select
      aria-label={ariaLabel}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value as SupportedLanguage)}
      className={
        className ??
        'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50'
      }
    >
      {includeUseCompanyDefault && (
        <option value="">{useCompanyDefaultLabel}</option>
      )}
      {offered.map((lng) => (
        <option key={lng} value={lng}>
          {LANGUAGE_LABELS[lng] ?? lng}
        </option>
      ))}
    </select>
  );
};

export default LanguageSelect;
